import React from "react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Mic, Zap, CheckSquare } from "lucide-react";

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16 space-y-16">
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-6xl font-bold">
            Work Less. Live More. Here's How.
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Forget the corporate grind. We're here to make work suck less—by doing it for you.
          </p>
        </div>

        {/* Steps Section */}
        <div className="grid md:grid-cols-3 gap-8 mt-16">
          {/* Step 1 */}
          <div className="space-y-4 p-6 rounded-lg border bg-card hover:shadow-lg transition-all duration-300">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Mic className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold">Start Recording. Go Back to Scrolling.</h3>
            <p className="text-muted-foreground">
              Press one button and let us handle the rest. Record your meeting, upload a transcript, 
              or drag in that 3-hour audio file that everyone ignored. We promise not to judge the 
              awkward silences or off-topic rants.
            </p>
            <div className="italic text-sm text-muted-foreground">
              "No more 'Can you repeat that?' moments."
            </div>
          </div>

          {/* Step 2 */}
          <div className="space-y-4 p-6 rounded-lg border bg-card hover:shadow-lg transition-all duration-300">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold">No Notes, No Problem.</h3>
            <p className="text-muted-foreground">
              Our AI listens better than your coworkers. It transcribes, analyzes, and spits out 
              summaries, action items, and even recurring tasks—all while you daydream about 
              quitting your job. We'll turn your chaos into order faster than you can say 
              'Let's circle back.'
            </p>
            <div className="italic text-sm text-muted-foreground">
              "AI: 100% drama-free, unlike Janet in Marketing."
            </div>
          </div>

          {/* Step 3 */}
          <div className="space-y-4 p-6 rounded-lg border bg-card hover:shadow-lg transition-all duration-300">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <CheckSquare className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-2xl font-semibold">Be the Hero Who Actually Followed Up.</h3>
            <p className="text-muted-foreground">
              Access beautifully organized meeting notes, gain insights, and track action items 
              effortlessly. Look like you've got it all together—even if you just woke up from a nap.
            </p>
            <div className="italic text-sm text-muted-foreground">
              "You're now the person people thank in emails. You're welcome."
            </div>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="space-y-8 mt-16">
          <h2 className="text-3xl font-bold text-center">Key Features That Make It Happen</h2>
          <div className="grid md:grid-cols-3 gap-8">
          <div className="space-y-2">
              <h4 className="font-semibold">Meeting Recording</h4>
              <p className="text-muted-foreground">
              Hit record, then forget about it. Our AI captures every awkward pause, side rant, and actual decision so you don’t have to.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Automated Meeting Notes</h4>
              <p className="text-muted-foreground">
              Ditch the notepad. Our AI captures every word, filters out the fluff, and delivers meeting notes that are actually useful—no effort required.”
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">Action Item Automation</h4>
              <p className="text-muted-foreground">
                No one likes typing up to-dos. Let our AI handle it. You're officially out of 
                the note-taking game.
              </p>
            </div>
            {/*<div className="space-y-2">
              <h4 className="font-semibold">Recurring Tasks</h4>
              <p className="text-muted-foreground">
                For those weekly status updates that no one actually reads—now automated. 
                You're free to finally catch up on Netflix.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-semibold">AI Chatbot</h4>
              <p className="text-muted-foreground">
                Ask the bot to summarize, analyze, or brainstorm. It's like an intern that 
                doesn't complain about unpaid labor.
              </p>
            </div>*/}
          </div>
        </div>

        {/* Workflow Section */}
        <div className="space-y-8 mt-16">
          <div className="max-w-3xl mx-auto space-y-4 text-center">
            <div className="flex flex-col items-center space-y-4">
              <div className="text-lg font-medium p-4 rounded-lg bg-primary/10 w-full">
                Step 1: Hit Record, Feel Accomplished.
              </div>
              <div className="h-8 w-0.5 bg-border"></div>
              <div className="text-lg font-medium p-4 rounded-lg bg-primary/10 w-full">
                Step 2: Relax While AI Works Harder Than Bob from IT.
              </div>
              <div className="h-8 w-0.5 bg-border"></div>
              <div className="text-lg font-medium p-4 rounded-lg bg-primary/10 w-full">
                Step 3: Share Notes and Bask in the Glory.
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <Button asChild size="lg" className="text-lg">
            <Link to="/sign-up">Stop Working. Start Using MeetMemoize</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;